import {Alert, Button, ThemeOptions} from "@mui/material";
import {ErrorBoundary, usePaletteMode, VCThemeProvider} from "@variocube/app-ui";
import React, {PropsWithChildren, useEffect} from "react";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import {SafecubeAuthProvider} from "./auth/auth-provider";
import {CookieConsentBanner} from "./component/cookie-consent-banner";
import {TrackingCodeInjector} from "./component/tracking-code-injector";
import {TenantContextProvider, useTenant} from "./context/tenant";
import {StorageLocalizationProvider, useLocalization} from "./i18n";
import {AuthCallback} from "./page/auth-callback";
import {AuthDebug} from "./page/auth-debug";
import {BookingRoutes} from "./page/booking-routes";
import {CubeResolver} from "./page/cube-resolver";
import {SiteList} from "./page/site-list";
import {SiteRoutes} from "./page/site-routes";
import {StorefrontPage} from "./page/storefront/storefront-page";

export function App() {
	return (
		<ErrorBoundary>
			<BrowserRouter>
				<SafecubeAuthProvider>
					<Routes>
						<Route path="/auth-callback" element={<AuthCallback />} />
						{import.meta.env.DEV && <Route path="/auth-debug" element={<AuthDebug />} />}
						<Route path="/cubes/:cubeId" element={<CubeResolver />} />
						<Route path="/cubes/:cubeId/:slug" element={<CubeResolver />} />
						<Route path="/*" element={<TenantScopedApp />} />
					</Routes>
				</SafecubeAuthProvider>
			</BrowserRouter>
		</ErrorBoundary>
	);
}

function TenantScopedApp() {
	return (
		<TenantContextProvider>
			<AppThemeProvider>
				<AppShell />
			</AppThemeProvider>
		</TenantContextProvider>
	);
}

function AppThemeProvider({children}: PropsWithChildren<unknown>) {
	const {tenant: {branding: {muiThemeOptions, ...branding} = {}} = {}} = useTenant();
	const {palette, ...options}: ThemeOptions = muiThemeOptions ?? {};

	return (
		<VCThemeProvider
			branding={{
				...branding,
				muiThemeOptions: {
					palette: {
						...(palette ?? {}),
						mode: "light",
					},
					...options,
				},
			}}
		>
			{children}
		</VCThemeProvider>
	);
}

function AppShell() {
	const {mode, setMode} = usePaletteMode();

	useEffect(() => {
		if (mode !== "light") {
			setMode("light");
			console.info("Forcing light mode until dark mode is supported.");
		}
	}, [setMode]);

	return (
		<StorageLocalizationProvider>
			<TrackingCodeInjector />
			<CookieConsentBanner />
			<Routes>
				<Route path="/:tenantId" element={<SiteList />} />
				<Route path="/:tenantId/storefront/:siteId" element={<StorefrontPage />} />
				<Route path="/:tenantId/bookings/:bookingId" element={<BookingRoutes />}>
					<Route path="/:tenantId/bookings/:bookingId/:view" element={<BookingRoutes />} />
				</Route>
				<Route path={"/:tenantId/:siteId"} element={<SiteRoutes />}>
					<Route path="/:tenantId/:siteId/:step" element={<SiteRoutes />} />
				</Route>
				<Route path="*" element={<NoMatchRoute />} />
			</Routes>
		</StorageLocalizationProvider>
	);
}

function NoMatchRoute() {
	const {t} = useLocalization();
	return (
		<Alert
			severity="error"
			action={<Button variant="contained" component="a" href="/">{t("common.toHomeCta")}</Button>}
		>
			{t("common.notFound")}
		</Alert>
	);
}
